/**
 * The browser's print bridge until the shells land (M4; 08-clients.md "Browser: no
 * direct hardware"): a terminal that owns printers "prints" each job by rendering its
 * PrintDoc to an HTML receipt (packages/device-bridge renderHtml) kept on screen,
 * where it can be printed or saved as a PDF with the browser's print dialog. The job
 * is confirmed as printed once rendered, so M3 demos run end to end without hardware.
 */
import type { PrintDoc } from '@qrkey/contracts';
import {
  renderHtml,
  type DeviceBridge,
  type PrinterRef,
  type RelaySeenStore,
} from '@qrkey/device-bridge';
import QRCode from 'qrcode';

export interface DevPrinted {
  readonly jobId: string;
  readonly printerId: string;
  readonly title: string;
  readonly html: string;
  readonly at: number;
}

const KEEP = 30;

/** SVG markup for every QR code of the document (rendered before the HTML, which is sync). */
async function qrSvgs(doc: PrintDoc): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  for (const b of doc.blocks) {
    if (b.t !== 'qr' || out.has(b.data)) continue;
    out.set(b.data, await QRCode.toString(b.data, { type: 'svg', margin: 1 }));
  }
  return out;
}

export class DevPrintBridge implements DeviceBridge {
  #printed: DevPrinted[] = [];
  readonly #listeners = new Set<() => void>();
  /** The job being printed (the relay prints one at a time). */
  current: string | null = null;

  info() {
    return Promise.resolve({ platform: 'web' as const, capabilities: ['print:html'] });
  }

  listPrinters() {
    return Promise.resolve([]);
  }

  async print(printer: PrinterRef, doc: PrintDoc) {
    const svgs = await qrSvgs(doc);
    const html = renderHtml(doc, { qrSvg: (data) => svgs.get(data) ?? '' });
    this.#printed = [
      {
        jobId: this.current ?? '',
        printerId: printer.printerId,
        title: doc.title,
        html,
        at: Date.now(),
      },
      ...this.#printed,
    ].slice(0, KEEP);
    this.#listeners.forEach((l) => l());
    return { ok: true };
  }

  openCashDrawer() {
    return Promise.resolve();
  }

  get printed(): readonly DevPrinted[] {
    return this.#printed;
  }

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  snapshot = (): readonly DevPrinted[] => this.#printed;
}

/**
 * Job ids this device printed, in localStorage: a reload, a reconnect or a re-sent job
 * never prints twice here (it only confirms again). Guarded: without storage the
 * memory copy still covers this tab.
 */
export function localSeenStore(terminalId: string, limit = 500): RelaySeenStore {
  const key = `qrkey.staff.printed.v1.${terminalId}`;
  let ids: string[] = [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) ids = parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    ids = [];
  }
  return {
    has: (id) => ids.includes(id),
    add(id) {
      if (ids.includes(id)) return;
      ids = [...ids, id].slice(-limit);
      try {
        window.localStorage.setItem(key, JSON.stringify(ids));
      } catch {
        // kept for this tab
      }
    },
  };
}
